import React, { useCallback, useEffect, useState } from 'react';
import { Icon, STATUS_MAP, StatusChip } from '../components/sp-primitives';
import { Modal } from '../components/sp-overlays';
import { PShell, useApp } from '../components/sp-shell';
import { ROUTES } from '../routes';

function PPaymentLogs() {
  const app = useApp();
  const activeCompanyId = Number(app.activeCompany?.id || 0);
  const company = app.companies.find((c) => c.id === activeCompanyId);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [queueStats, setQueueStats] = useState(null);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  const permissions = Array.isArray(app.currentUser?.permissions) ? app.currentUser.permissions : [];
  const canView = permissions.includes('Aprovador') || permissions.includes('Administrador');

  const fmtOccurred = (d) => {
    if (!d) return '—';
    const s = String(d).trim();
    const x = s.includes('T') || s.includes('Z') || s.includes('+')
      ? new Date(s)
      : new Date(s.replace(' ', 'T') + 'Z');
    if (Number.isNaN(x.getTime())) return '—';
    return x.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const statusChip = (s) => {
    if (s === 'success') return <StatusChip kind="green">Sucesso</StatusChip>;
    if (s === 'failed') return <StatusChip kind="red">Erro</StatusChip>;
    if (s === 'processing') return <StatusChip kind={STATUS_MAP.processing.kind}>Processando</StatusChip>;
    return <StatusChip kind="neutral">{s || '—'}</StatusChip>;
  };

  const httpChip = (code) => {
    if (!code) return '—';
    if (code === 201) return <span className="sp-chip sp-chip-green">201</span>;
    if (code >= 500) return <span className="sp-chip sp-chip-red">{code}</span>;
    if (code >= 400) return <span className="sp-chip sp-chip-amber">{code}</span>;
    return <span className="sp-chip">{code}</span>;
  };

  const fetchLogs = useCallback(async (p) => {
    if (!activeCompanyId) return;
    try {
      const query = { company_id: activeCompanyId, page: String(p), per_page: '20' };
      if (statusFilter) query.status = statusFilter;
      const data = await app.fetchPaymentSyncLogs(query);
      setPayload(data);
    } catch (e) {
      setErr(e.message || 'Falha ao carregar logs');
      setPayload(null);
    }
  }, [app, activeCompanyId, statusFilter]);

  useEffect(() => {
    if (!app.workspaceReady) return;
    if (!canView) {
      app.navigate(ROUTES.DASHBOARD);
      return;
    }
    setLoading(true);
    setErr(null);
    fetchLogs(page).finally(() => setLoading(false));
  }, [app.workspaceReady, page, fetchLogs, canView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time polling: sync logs + queue stats
  useEffect(() => {
    if (!activeCompanyId || !app.authToken) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const [logsData, stats] = await Promise.all([
          app.fetchPaymentSyncLogs({ company_id: activeCompanyId, page: String(page), per_page: '20', ...(statusFilter ? { status: statusFilter } : {}) }),
          app.fetchPaymentQueueStats(activeCompanyId),
        ]);
        if (!cancelled) {
          setPayload(logsData);
          setQueueStats(stats);
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeCompanyId, page, app.authToken, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!app.workspaceReady) {
    return (
      <PShell active="payments">
        <div className="sp-screen-head">
          <h1 className="sp-screen-head__title">A carregar…</h1>
        </div>
      </PShell>
    );
  }
  if (!canView) return null;

  const rows = payload?.data || [];
  const lastPage = payload?.last_page || 1;

  const q = queueStats?.queue || {};
  const pendingPayments = queueStats?.pending_payments || [];
  const hasQueueActivity = (q.queued ?? 0) > 0 || (q.processing ?? 0) > 0;

  return (
    <PShell active="payments">
      <button type="button" className="sp-btn sp-btn-ghost sp-form-back" onClick={() => app.navigate(ROUTES.APPROVE)}>
        ← Voltar para aprovações
      </button>
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Fila de transmissão Sienge</div>
          <h1 className="sp-screen-head__title">Logs de Pagamentos — {company?.name || `Empresa #${activeCompanyId}`}</h1>
          <div className="sp-screen-head__meta sp-mono">
            Na Fila: {q.queued ?? '—'} · Processando: {q.processing ?? '—'} · Falhas: {q.failed ?? '—'} · POST /v1/bills · fila "payments" no sp_cron_jobs
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="sp-input"
            style={{ width: 150 }}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">Todos os status</option>
            <option value="success">Sucesso</option>
            <option value="failed">Falha</option>
            <option value="processing">Processando</option>
          </select>
        </div>
      </div>

      {hasQueueActivity && !loading && (
        <div className="sp-sync-live-chip-wrap">
          <span className="sp-sync-live-chip">Fila de Transmissão Ativa - Acompanhe Abaixo</span>
        </div>
      )}

      {pendingPayments.length > 0 && (
        <div className="sp-card sp-card-flush sp-card-creditors" style={{ marginBottom: 12 }}>
          <div className="sp-batch-table-head">Pagamentos na Fila</div>
          <table className="sp-table">
            <thead>
              <tr>
                <th>Lote</th>
                <th>Pagamento</th>
                <th>Credor</th>
                <th className="sp-th-num">Valor</th>
                <th>Status</th>
                <th>Tent.</th>
                <th>Doc. Sienge</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pendingPayments.map((p) => {
                const amount = Number(p.sienge_totalInvoiceAmount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                const isPaused = p.sienge_sync_status === 'paused';
                const statusLabel = isPaused ? 'Pausado' : p.sienge_sync_status === 'pending' ? 'Aguardando CRON' : p.sienge_sync_status === 'processing' ? 'Processando' : p.sienge_sync_status;
                const statusKind = isPaused ? 'neutral' : p.sienge_sync_status === 'pending' ? 'amber' : p.sienge_sync_status === 'processing' ? 'neutral' : 'red';
                return (
                  <tr key={`p-${p.id}`}>
                    <td className="sp-mono sp-fw-700">L-{p.lot_id}</td>
                    <td className="sp-mono">{p.id}</td>
                    <td>{p.creditor_name || '—'}</td>
                    <td className="sp-num sp-td-num">{amount}</td>
                    <td><StatusChip kind={statusKind}>{statusLabel}</StatusChip></td>
                    <td className="sp-mono">{p.sienge_sync_attempts || 0}</td>
                    <td className="sp-mono">{p.sienge_document_id || '—'}</td>
                    <td>
                      {(p.sienge_sync_status === 'failed') && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-accent"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={async () => {
                            try {
                              await app.togglePaymentPause(p.id, 'retry');
                              const stats = await app.fetchPaymentQueueStats(activeCompanyId);
                              setQueueStats(stats);
                            } catch (e) {
                              app.toast('error', 'Erro', e?.message || 'Falha ao re-enfileirar.');
                            }
                          }}
                        >
                          Tentar Novamente
                        </button>
                      )}
                      {['pending', 'processing'].includes(p.sienge_sync_status) && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-outline"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={async () => {
                            try {
                              await app.togglePaymentPause(p.id, 'pause');
                              const stats = await app.fetchPaymentQueueStats(activeCompanyId);
                              setQueueStats(stats);
                            } catch (e) {
                              app.toast('error', 'Erro', e?.message || 'Falha ao pausar.');
                            }
                          }}
                        >
                          Pausar
                        </button>
                      )}
                      {p.sienge_sync_status === 'paused' && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-accent"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={async () => {
                            try {
                              await app.togglePaymentPause(p.id, 'resume');
                              const stats = await app.fetchPaymentQueueStats(activeCompanyId);
                              setQueueStats(stats);
                            } catch (e) {
                              app.toast('error', 'Erro', e?.message || 'Falha ao retomar.');
                            }
                          }}
                        >
                          Retomar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="sp-card sp-card-flush sp-card-creditors">
        <div className="sp-batch-table-head">Histórico de Execuções</div>
        {err && <div className="sp-creditors-banner sp-creditors-banner--err">{err}</div>}
        {loading && !err && <div className="sp-creditors-banner">Carregando…</div>}
        <table className="sp-table">
          <thead>
            <tr>
              <th>Lote</th>
              <th>Pagamento</th>
              <th>Credor</th>
              <th className="sp-th-num">Valor</th>
              <th>Tent.</th>
              <th>HTTP</th>
              <th>Duração</th>
              <th>Data / Hora</th>
              <th>Status</th>
              <th>Erro</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.map((log) => {
              const payment = log.payment || {};
              const lot = log.lot || {};
              const amount = Number(payment.sienge_totalInvoiceAmount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
              const duration = log.duration_ms ? `${log.duration_ms}ms` : '—';
              const hasError = log.error_message && log.error_message.length > 0;
              const error = hasError
                ? (log.error_message.length > 80 ? log.error_message.slice(0, 80) + '…' : log.error_message)
                : '—';

              return (
                <tr key={log.id}>
                  <td className="sp-mono sp-fw-700">{lot.id ? `L-${lot.id}` : '—'}</td>
                  <td className="sp-mono">{payment.id || '—'}</td>
                  <td>{payment.creditor_name || '—'}</td>
                  <td className="sp-num sp-td-num">{amount}</td>
                  <td className="sp-mono">{log.attempt || 1}</td>
                  <td>{httpChip(log.sienge_status_code)}</td>
                  <td className="sp-mono">{duration}</td>
                  <td className="sp-mono sp-sync-log-dt">{fmtOccurred(log.created_at)}</td>
                  <td>{statusChip(log.status)}</td>
                  <td className="sp-mono sp-sync-log-details" title={log.error_message || ''}>
                    {hasError ? (
                      <button type="button" className="sp-approve-debug-toggle" onClick={() => setErrorModal({ open: true, message: log.error_message })}>
                        {error}
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
            {!loading && !err && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="sp-td-empty">
                  Nenhum registo de transmissão para esta empresa.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {lastPage > 1 && (
          <div className="sp-creditors-pager">
            <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Anterior
            </button>
            <span className="sp-mono">
              Página {page} de {lastPage}
            </span>
            <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              Próxima
            </button>
          </div>
        )}
      </div>
      <Modal open={errorModal.open} onClose={() => setErrorModal({ open: false, message: '' })} width={600}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Detalhes do Erro</div>
            <div className="sp-modal__head-desc">Mensagem completa retornada na tentativa de transmissão.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => setErrorModal({ open: false, message: '' })}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body" style={{ maxHeight: 400, overflowY: 'auto' }}>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.6, margin: 0, fontFamily: 'var(--font-mono)' }}>
            {errorModal.message}
          </pre>
        </div>
      </Modal>
    </PShell>
  );
}

export { PPaymentLogs };
